/**
 * wholesaleForm.js
 *
 * @version 1.1.0
 * @author …
 * @license …
 */

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */
const SEL = {
	form: '#wholesaleForm',
	radios: '.wholesale__input',
	newBlocks: '.form--new',
	checkboxes: '.form__checkbox--interest',
	hiddenInterest: '.form__interest',
	newPanel: '.js-wholesale-new',
	existingPanel: '.js-wholesale-existing',
	modal: '.js-modal--wholesale',
	confirmNew: '.js-wholesale-confirmation--new',
	confirmExisting: '.js-wholesale-confirmation--existing',
};

const STORAGE_KEY = 'lastst.wholesale.customerType';

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */
function rememberOriginalType(el) {
	if (!el.dataset.origType) el.dataset.origType = el.type || 'text';
}

function getSelectedCustomerType(form) {
	return form.querySelector(`${SEL.radios}:checked`)?.value || 'New';
}

function updateInterestField(form) {
	const hidden = form.querySelector(SEL.hiddenInterest);
	const value = Array.from(form.querySelectorAll(`${SEL.checkboxes}:checked`))
		.map((el) => el.value)
		.join(', ');
	if (hidden) hidden.value = value;
}

function syncConfirmationModal(customerType) {
	const modal = document.querySelector(SEL.modal);
	if (!modal) return;

	const isExisting = customerType === 'Existing';
	const newEl = modal.querySelector(SEL.confirmNew);
	const existingEl = modal.querySelector(SEL.confirmExisting);

	if (newEl) {
		newEl.classList.toggle('hide', isExisting);
		newEl.setAttribute('aria-hidden', String(isExisting));
	}
	if (existingEl) {
		existingEl.classList.toggle('hide', !isExisting);
		existingEl.setAttribute('aria-hidden', String(!isExisting));
	}
}

function persistCustomerType(form) {
	const type = getSelectedCustomerType(form);
	try { sessionStorage.setItem(STORAGE_KEY, type); } catch (_) { /* no-op */ }
}

function toggleByCustomerType(form) {
	const isNew = form.querySelector(`${SEL.radios}[value="New"]`)?.checked;

	/* Show / hide the extra fields  */
	form.querySelectorAll(SEL.newBlocks).forEach((block) => {
		block.classList.toggle('hide', !isNew);

		block.querySelectorAll('input, textarea, select').forEach((el) => {
			/* Only <input> elements have a “type” we can safely overwrite */
			if (el instanceof HTMLInputElement) {
				el.type = isNew ? el.dataset.origType : 'hidden';
			}

			/* Disable all controls when the block is hidden */
			el.disabled = !isNew;
		});
	});

	/* Swap the whole fields/submit block for the Existing-customer panel */
	form.querySelector(SEL.newPanel)?.classList.toggle('hide', !isNew);
	const existing = form.querySelector(SEL.existingPanel);
	if (existing) {
		existing.classList.toggle('hide', isNew);
		existing.setAttribute('aria-hidden', String(isNew));
	}

	/* Aggregated interest hidden field  */
	const agg = form.querySelector(SEL.hiddenInterest);
	if (agg) {
		agg.type = isNew ? agg.dataset.origType : 'hidden';
		agg.classList.toggle('hide', !isNew);
	}
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Attach wholesaleForm behaviour to `document`.
 *
 * @return {() => void}
 */
function initWholesaleForm() {
	const form = document.querySelector(SEL.form);
	if (!form) return () => { };

	/* Cache original input types on first load */
	form.querySelectorAll(`${SEL.newBlocks} input, ${SEL.newBlocks} textarea, ${SEL.newBlocks} select`).forEach(rememberOriginalType);
	form.querySelector(SEL.hiddenInterest) && rememberOriginalType(form.querySelector(SEL.hiddenInterest));

	/* Initial UI sync */
	toggleByCustomerType(form);

	/* Read the last selected customer type from sessionStorage (set on submit) */
	let storedType = null;
	try { storedType = sessionStorage.getItem(STORAGE_KEY); } catch (_) { /* no-op */ }
	if (storedType) {
		syncConfirmationModal(storedType);
		try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* no-op */ }
	} else {
		/* Fallback: use whatever is currently selected (default "New") */
		syncConfirmationModal(getSelectedCustomerType(form));
	}

	/* Delegated listeners */
	const onChange = (e) => {
		if (e.target.matches(SEL.checkboxes)) updateInterestField(form);
		if (e.target.matches(SEL.radios)) toggleByCustomerType(form);
	};

	const onSubmit = () => {
		updateInterestField(form);
		persistCustomerType(form);
	};

	form.addEventListener('change', onChange, true);
	form.addEventListener('submit', onSubmit, true);
}

initWholesaleForm();
